/* Coast Creative — front-end behaviors
   <coast-slider> : horizontal product slider with arrow controls
   + a coast-blue progress bar that tracks scroll position. */

class CoastSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-coast-track]');
    if (!this.track) return;

    this.fill = this.querySelector('.coast-slider__progress i');
    this.prevBtn = this.querySelector('.coast-snav[data-dir="prev"]');
    this.nextBtn = this.querySelector('.coast-snav[data-dir="next"]');

    this.update = this.update.bind(this);

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));
    this.track.addEventListener('scroll', this.update, { passive: true });
    window.addEventListener('resize', this.update);

    this.update();
  }

  step(dir) {
    const amount = Math.max(260, this.track.clientWidth * 0.8);
    this.track.scrollBy({ left: dir * amount, behavior: 'smooth' });
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const p = max > 1 ? this.track.scrollLeft / max : 0;
    if (this.fill) this.fill.style.width = Math.max(8, Math.round(p * 100)) + '%';
    if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= 2;
    if (this.nextBtn) this.nextBtn.disabled = this.track.scrollLeft >= max - 2;
  }
}

if (!customElements.get('coast-slider')) {
  customElements.define('coast-slider', CoastSlider);
}

/* Open external links (different domain) in a new tab. Shopify menus have no
   per-link "new tab" option, so we detect them by hostname at render time. */
(function () {
  function markExternalLinks(root) {
    var host = window.location.hostname;
    var links = (root || document).querySelectorAll('a[href]');
    Array.prototype.forEach.call(links, function (a) {
      var isHttp = a.protocol === 'http:' || a.protocol === 'https:';
      if (!isHttp || !a.hostname || a.hostname === host) return;      // internal / mailto / tel — leave alone
      if (a.target === '_blank') return;                              // already opens in a new tab
      a.target = '_blank';
      var rel = (a.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
      if (rel.indexOf('noopener') === -1) rel.push('noopener');
      a.setAttribute('rel', rel.join(' '));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { markExternalLinks(); }, { once: true });
  } else {
    markExternalLinks();
  }
})();

/* <coast-terminal> — replays the static terminal lines as a live typing session.
   Types each command char-by-char, prints its output, scrolls (dropping old
   lines like a real terminal), then loops. Falls back to the static content
   when animation is off or the visitor prefers reduced motion; pauses off-screen. */
class CoastTerminal extends HTMLElement {
  connectedCallback() {
    if (this.dataset.animate !== 'true') return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.steps = this.parseSteps();
    if (!this.steps.length) return;

    this.running = false;
    this.gen = 0;
    this.observer = new IntersectionObserver(
      (entries) => { entries[0].isIntersecting ? this.start() : this.stop(); },
      { threshold: 0.2 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.stop();
    if (this.observer) this.observer.disconnect();
  }

  // Read the server-rendered lines into [{ cmd, out: [html, ...] }] steps.
  parseSteps() {
    const steps = [];
    let current = null;
    this.querySelectorAll('.ct-line').forEach((el) => {
      if (el.classList.contains('ct-cmd')) {
        current = { cmd: el.getAttribute('data-cmd') || '', out: [] };
        steps.push(current);
      } else {
        if (!current) { current = { cmd: '', out: [] }; steps.push(current); }
        current.out.push(el.outerHTML);
      }
    });
    return steps;
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.loop(++this.gen);
  }

  stop() { this.running = false; }

  sleep(ms) { return new Promise((resolve) => setTimeout(resolve, ms)); }

  // Keep the newest content visible: drop lines off the top when they overflow.
  trim() {
    let guard = 0;
    while (this.scrollHeight > this.clientHeight + 2 && this.firstElementChild && guard < 300) {
      this.removeChild(this.firstElementChild);
      guard++;
    }
  }

  async typeCommand(cmd) {
    const line = document.createElement('div');
    line.className = 'ct-line ct-cmd';
    line.innerHTML = '<span class="cp">&rsaquo;</span> <span class="ct-typed"></span><span class="ct-cursor">&#9613;</span>';
    this.appendChild(line);
    this.trim();
    const typed = line.querySelector('.ct-typed');
    for (let i = 0; i < cmd.length; i++) {
      if (!this.running) return;
      typed.textContent += cmd.charAt(i);
      await this.sleep(38 + Math.random() * 55);
    }
    await this.sleep(280);
    const cursor = line.querySelector('.ct-cursor');
    if (cursor) cursor.remove();
  }

  printLine(html) {
    const holder = document.createElement('div');
    holder.innerHTML = html.trim();
    const node = holder.firstElementChild;
    if (node) { this.appendChild(node); this.trim(); }
  }

  async loop(gen) {
    while (this.running && gen === this.gen) {
      this.innerHTML = '';
      for (const step of this.steps) {
        if (!this.running || gen !== this.gen) return;
        if (step.cmd) await this.typeCommand(step.cmd);
        for (const html of step.out) {
          if (!this.running || gen !== this.gen) return;
          this.printLine(html);
          await this.sleep(140 + Math.random() * 130);
        }
        await this.sleep(700);
      }
      await this.sleep(1900);
    }
  }
}

if (!customElements.get('coast-terminal')) {
  customElements.define('coast-terminal', CoastTerminal);
}
